/* global safeArray, num, taxReconRound */
// SKATT OG OFFENTLIGE AVGIFTER — seksjoner, betalinger, poster og kontroller
//
// Skilt fra backend-tax-recon-view.jsx, som eier kontrollkontoene og
// sammenstillingen, og fra -parts.jsx, som eier kravene. Her ligger de fire
// seksjonene under hovedavstemmingen (forskuddstrekk, arbeidsgiveravgift,
// MVA-oppgjoer og selskapsskatt) og de fire utledningene skjermen bruker til aa
// si HVA som gjenstaar: betalingene og direktefoeringene, postene som krever en
// vurdering, datakildene og de automatiske kontrollene.
//
// Ingen av dem regner ut et fagtall. De leser kravene og hovedboken modellen
// allerede har bygget, og teller.

// ------------------------------------------------------------------
// Betalinger og direkteføringer
// ------------------------------------------------------------------
// Betalingene er de registrerte oppgjørene mot kravene. Direkteføringene er
// posteringene på kontrollkontoene som IKKE kommer fra en lønnskjøring, en
// registrert skattebetaling eller et MVA-oppgjør — et gebyr, en rente eller en
// manuell korrigering. Det er nettopp dem avstemmingen skal la noen se på.
const TAX_RECON_KNOWN_SOURCES = ['payrollRun', 'payrollTaxPayment', 'vatSettlement'];

const taxReconPaymentRows = ({ claims, lines, range }) => {
  const rows = [];
  for (const claim of claims) {
    for (const payment of safeArray(claim.payments)) {
      const date = String(payment.date || payment.paidAt || '').slice(0, 10);
      const amount = claim.kind === 'aga' ? num(payment.agaAmount)
        : claim.kind === 'forskuddstrekk' ? num(payment.taxAmount)
          : Math.abs(num(payment.amount));
      if (!amount) continue;
      const late = Boolean(date && claim.dueDate && date > claim.dueDate);
      rows.push({
        id: 'betaling-' + claim.id + '-' + (payment.id || date),
        itemId: claim.id,
        date,
        description: payment.note || t('rcs.payment.description', { title: claim.title.toLowerCase() }),
        amount: taxReconRound(amount),
        claimTitle: claim.title,
        match: late ? 'etter_frist' : date > range.to ? 'etter_periode' : 'registrert',
        // Banklinjen som bekrefter at pengene forlot kontoen. Uten den er
        // betalingen registrert, ikke dokumentert — og det er forskjellen
        // mellom foretakets egen paastand og en ekstern kilde.
        bank: payment.bank || null,
        difference: 0,
      });
    }
  }

  // Direkteføringene. Kilden er bilagets egen (`sourceType`), ikke teksten:
  // et bilag som er ført manuelt på 2950 ER en direkteføring, uansett hva noen
  // skrev i beskrivelsen.
  for (const line of safeArray(lines)) {
    if (TAX_RECON_KNOWN_SOURCES.includes(String(line.sourceType || ''))) continue;
    rows.push({
      id: 'direkte-' + line.id,
      itemId: 'direkte-' + line.id,
      date: line.date,
      description: line.description || line.voucherName || t('rcs.payment.directPosting'),
      amount: taxReconRound(line.amount),
      claimTitle: line.accountCode + ' ' + (line.accountName || ''),
      match: 'direktefort',
      bank: null,
      difference: 0,
      voucherNumber: line.voucherNumber || '',
      accountCode: line.accountCode,
    });
  }

  return rows.sort((left, right) => String(left.date).localeCompare(String(right.date)));
};

// Postene som skal vurderes: kravene med varsel, og hver direkteføring. En
// direkteføring er ikke i seg selv en feil — den er en postering ingen automatisk
// kilde svarer for, og derfor noe byrået må ta stilling til.
const taxReconItems = ({ claims, payments }) => {
  const items = claims
    .filter((claim) => claim.flags.length)
    .map((claim) => ({
      id: claim.id,
      kind: claim.kind,
      title: claim.title,
      periodLabel: claim.periodLabel,
      dueDate: claim.dueDate,
      reported: claim.reported,
      booked: claim.booked,
      paid: claim.paid,
      open: claim.open,
      flags: claim.flags,
    }));
  for (const row of payments.filter((entry) => entry.match === 'direktefort')) {
    items.push({
      id: row.id,
      kind: 'direktefort',
      title: row.description,
      periodLabel: row.claimTitle,
      dueDate: '',
      reported: null,
      booked: row.amount,
      paid: null,
      open: null,
      flags: ['direktefort'],
    });
  }
  return items;
};


// ------------------------------------------------------------------
// Datakilder og automatiske kontroller
// ------------------------------------------------------------------

const taxReconSources = ({ state, range, claims, controlAccounts, term, runs, bankCoverage }) => {
  const reported = runs.filter((run) => run.altinnSent).length;
  return [
    {
      key: 'hovedbok', label: t('rcs.source.ledger'),
      complete: controlAccounts.rows.some((row) => row.status !== 'ikke_relevant'),
      detail: t('rcs.source.ledgerDetail', { count: controlAccounts.lines.length }),
    },
    {
      key: 'lonn', label: t('rcs.source.payroll'),
      complete: runs.length > 0,
      detail: runs.length
        ? t(runs.length === 1 ? 'rcs.source.payrollDetailOne' : 'rcs.source.payrollDetailMany', { count: runs.length })
        : t('rcs.source.payrollDetailNone'),
    },
    {
      key: 'amelding', label: t('rcs.source.amelding'),
      complete: runs.length === 0 || reported === runs.length,
      detail: runs.length === 0
        ? t('rcs.source.ameldingNone')
        : t('rcs.source.ameldingDetail', { reported, total: runs.length }),
    },
    {
      key: 'mva', label: t('rcs.source.vat'),
      complete: Boolean(term && (term.status === 'levert' || term.status === 'levert-betalt')),
      detail: term ? t('rcs.source.vatDetail', { period: term.period, year: term.year, due: term.frist }) : t('rcs.vat.noTerm'),
    },
    {
      key: 'betaling', label: t('rcs.source.payments'),
      complete: claims.every((claim) => claim.openAtPeriodEnd <= 0.005
        || !claim.dueDate || claim.dueDate > range.to),
      detail: t('rcs.source.paymentsDetail', { count: claims.filter((claim) => claim.paid > 0.005).length }),
    },
    {
      // Bankutskriften er den ene EKSTERNE kilden omraadet har. Den svarer ikke
      // paa om beloepet er riktig, men paa om det faktisk ble betalt.
      key: 'bank', label: t('rcs.source.bank'),
      complete: bankCoverage && claims.every(
        (claim) => !claim.bankCover || claim.bankCover.total === 0 || claim.bankCover.complete,
      ),
      detail: !bankCoverage
        ? t('rcs.source.bankNone')
        : t('rcs.source.bankDetail', {
          covered: claims.reduce((sum, claim) => sum + (claim.bankCover ? claim.bankCover.covered : 0), 0),
          total: claims.reduce((sum, claim) => sum + (claim.bankCover ? claim.bankCover.total : 0), 0),
        }),
    },
    {
      // Den ene kilden avstemmingen IKKE har. Uten den sammenliknes foretakets
      // egne beregnede krav med foretakets egen bokføring — ikke med det
      // Skatteetaten faktisk har fastsatt.
      key: 'skatteetaten', label: t('rcs.source.skatteetaten'),
      complete: false,
      detail: t('rcs.source.skatteetatenDetail'),
      external: true,
    },
  ];
};

const taxReconChecks = ({ claims, controlAccounts, counts, term, range, runs, bankCoverage }) => [
  {
    key: 'kontoer', label: t('rcs.check.accounts'),
    value: controlAccounts.rows.filter((row) => !row.inArea && row.status !== 'ikke_relevant').length,
  },
  { key: 'saldo', label: t('rcs.check.balance'), value: counts.accountDeviations },
  {
    key: 'trekk_lonn', label: t('rcs.check.withholdingPayroll'),
    value: counts.ikkeMeldt,
  },
  { key: 'trekk_bank', label: t('rcs.check.withholdingPayments'), value: counts.betalingMangler },
  { key: 'frister', label: t('rcs.check.withholdingDeadlines'), value: counts.betaltForSent },
  { key: 'aga', label: t('rcs.check.aga'), value: counts.bokforingsdifferanse },
  {
    key: 'aga_frist', label: t('rcs.check.agaDeadline'),
    value: claims.filter((claim) => claim.kind === 'aga' && claim.flags.includes('betaling_mangler')).length,
  },
  {
    key: 'mva', label: t('rcs.check.vat'),
    value: term && range.monthIndex === term.months[1]
      && term.status !== 'levert' && term.status !== 'levert-betalt' ? 1 : 0,
  },
  { key: 'delvis', label: t('rcs.check.partial'), value: counts.delvisBetalt },
  {
    // Uten en bankutskrift som dekker perioden er dette ikke en kontroll som
    // fant null — det er en kontroll som ikke kunne kjores.
    key: 'bankdekning',
    label: bankCoverage
      ? t('rcs.check.bankCoverage')
      : t('rcs.check.bankCoverageNone'),
    value: counts.utenBankdekning,
  },
  { key: 'direkte', label: t('rcs.check.direct'), value: counts.direktefort },
  { key: 'forfalt', label: t('rcs.check.overdue'), value: counts.betalingMangler },
  {
    key: 'ubokfort', label: t('rcs.check.unbooked'), value: counts.ikkeBokfort,
  },
  {
    key: 'lonn_kjoringer', label: t('rcs.check.payrollBooked'),
    value: safeArray(runs).filter((run) => run.status === 'draft').length,
  },
];

// ------------------------------------------------------------------
// Seksjonene under kravene
// ------------------------------------------------------------------

// Forskuddstrekk per lønnskjøring: trekket, fristen, og det som faktisk er
// betalt. Appen registrerer betalingen mot kjøringen, ikke mot en KID —
// Skatteetatens KID hentes ikke, og en tom kolonne som later som den er
// kontrollert ville vært verre enn ingen.
const taxReconTrekkRows = ({ claims, range }) => claims
  .filter((claim) => claim.kind === 'forskuddstrekk')
  .map((claim) => ({
    id: claim.id,
    runId: claim.runId,
    label: claim.runLabel,
    payDate: claim.claimDate,
    withholding: claim.reported,
    dueDate: claim.dueDate,
    paidAmount: claim.paid,
    paidDate: claim.lastPaidDate,
    difference: taxReconRound(claim.reported - claim.paid),
    reported: claim.reportedLabel,
    status: claim.paymentStatus,
    flags: claim.flags,
    afterPeriod: Boolean(claim.lastPaidDate) && claim.lastPaidDate > range.to,
  }));

// Arbeidsgiveravgift og finansskatt. Finansskatt er ikke en del av appens
// lønnsmodell, og seksjonen sier det i stedet for å vise en null som kunne blitt
// lest som «kontrollert og lik null».
const taxReconAgaSection = ({ claims, range, agaBooked, agaCost }) => {
  const current = claims.find((claim) => claim.kind === 'aga' && claim.inPeriod) || null;
  const computed = current ? current.reported : 0;
  const difference = current && agaBooked !== null ? taxReconRound(agaBooked - computed) : null;
  return {
    term: current ? current.term : null,
    termLabel: current ? current.periodLabel : '',
    computed,
    booked: agaBooked === null || agaBooked === undefined ? null : taxReconRound(agaBooked),
    cost: agaCost === null || agaCost === undefined ? null : taxReconRound(agaCost),
    difference,
    dueDate: current ? current.dueDate : '',
    paid: current ? current.paid : 0,
    open: current ? current.open : 0,
    paymentStatus: current ? current.paymentStatus : { id: 'noFee', label: t('rcs.payStatus.noFee'), variant: 'muted' },
    periodEnd: range.to,
    hasFinanceTax: false,
  };
};

// MVA-oppgjøret slik skatteområdet trenger det: status, beløp og
// oppgjørskontoen. Kontrollfunnene ligger i MVA-avstemmingen.
const taxReconVatSection = ({ claim, term, settlementBalance }) => ({
  term: term ? term.n : null,
  termLabel: term ? term.period + ' ' + term.year : '',
  dueLabel: term ? term.frist : '',
  status: claim ? claim.reportedLabel : t('rcs.vat.noTerm'),
  submitted: Boolean(claim && claim.submitted),
  net: claim ? claim.draft : null,
  direction: claim ? claim.direction : 'betale',
  paid: claim ? claim.paid : 0,
  open: claim ? claim.open : 0,
  settlementBalance: settlementBalance === null || settlementBalance === undefined
    ? null : taxReconRound(settlementBalance),
  termDue: Boolean(claim && claim.termDue),
});

// Selskapsskatt og forskuddsskatt. Den månedlige skatteavsetningen er en
// regnskapsmessig beregning — den avstemmes ikke mot noe eksternt før melding om
// forskudd eller skatteoppgjøret foreligger, og Plumme henter ingen av dem.
// Seksjonen viser derfor hovedbokens egne tall og sier hva som ikke er
// kontrollert.
const taxReconCompanyTaxSection = ({ accountsByCode, taxCost }) => {
  const balance = (code) => {
    const row = accountsByCode.get(code);
    return row ? row.closing : null;
  };
  return {
    payable: balance('2500'),
    deferred: balance('2510'),
    prepaid: balance('2540'),
    cost: taxCost === null || taxCost === undefined ? null : taxReconRound(taxCost),
    externalSource: false,
  };
};

Object.assign(window, {
  taxReconPaymentRows, taxReconItems, taxReconSources, taxReconChecks,
  taxReconTrekkRows, taxReconAgaSection, taxReconVatSection, taxReconCompanyTaxSection,
  TAX_RECON_KNOWN_SOURCES,
});
